import { useEffect, useState } from 'react';
import { Box, Button, Group, Text, TextInput, Loader, Center, Tabs, Textarea } from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { useNote, useCreateNote, useUpdateNote } from '../api/notes';
import TipTapEditor from '../components/TipTapEditor';
import './NotesEditPage.css';

interface NotesEditPageProps {
  noteId: string | null;
  onBack: () => void;
}

export default function NotesEditPage({ noteId, onBack }: NotesEditPageProps) {
  const isNewNote = noteId === '__new__' || !noteId;

  // Fetch existing note if editing
  const { data: existingNote, isLoading: noteLoading } = useNote(
    isNewNote ? undefined : noteId || undefined
  );

  // Mutations
  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote(noteId || '');

  // Form state
  const [title, setTitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [status, setStatus] = useState<'draft' | 'archived'>('draft');
  const [category, setCategory] = useState<'uncategorized' | 'job'>('uncategorized');

  // View state
  const [editorView, setEditorView] = useState<'wysiwyg' | 'markdown'>('wysiwyg');

  // Initialize form with existing note data
  useEffect(() => {
    if (existingNote && !isNewNote) {
      setTitle(existingNote.title);
      setMarkdown(existingNote.content_markdown);
      setStatus(existingNote.status as any);
      setCategory(existingNote.category as any);
    }
  }, [existingNote, isNewNote]);

  const handleSave = async () => {
    if (!title.trim() || !markdown.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    try {
      if (isNewNote) {
        // Create new note - always save as markdown
        await createNoteMutation.mutateAsync({
          title: title.trim(),
          content_markdown: markdown,
          status,
          category,
        });
      } else if (noteId) {
        // Update existing note - always save as markdown
        await updateNoteMutation.mutateAsync({
          title: title.trim(),
          content_markdown: markdown,
          status,
          category,
        });
      }

      onBack();
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    }
  };

  const isLoading = noteLoading || createNoteMutation.isPending || updateNoteMutation.isPending;
  const hasChanges =
    isNewNote ||
    title !== (existingNote?.title || '') ||
    markdown !== (existingNote?.content_markdown || '') ||
    status !== (existingNote?.status || 'draft') ||
    category !== (existingNote?.category || 'uncategorized');

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Box p="md" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', flexShrink: 0 }}>
        <Group justify="space-between" align="center">
          <Group gap="md">
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconArrowLeft size={18} />}
              onClick={onBack}
            >
              Back
            </Button>
            <Text fw={700} size="lg">
              {isNewNote ? 'Create Note' : 'Edit Note'}
            </Text>
          </Group>
          <Button
            leftSection={<IconCheck size={18} />}
            onClick={handleSave}
            loading={isLoading}
            disabled={!hasChanges || !title.trim() || !markdown.trim()}
          >
            Save
          </Button>
        </Group>
      </Box>

      {/* Form Controls */}
      <Box p="md" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', flexShrink: 0 }}>
        <TextInput
          label="Title"
          placeholder="Enter note title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
      </Box>

      {/* Editor Tabs */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {noteLoading ? (
          <Center style={{ height: '100%' }}>
            <Loader />
          </Center>
        ) : (
          <Tabs value={editorView} onChange={(val) => setEditorView((val as any) || 'wysiwyg')} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Tabs.List>
              <Tabs.Tab value="wysiwyg">WYSIWYG Editor</Tabs.Tab>
              <Tabs.Tab value="markdown">Markdown</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="wysiwyg" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <TipTapEditor markdown={markdown} onMarkdownChange={setMarkdown} />
            </Tabs.Panel>

            <Tabs.Panel value="markdown" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Textarea
                placeholder="Enter markdown content..."
                value={markdown}
                onChange={(e) => setMarkdown(e.currentTarget.value)}
                style={{ flex: 1, height: '100%', overflow: 'hidden' }}
                styles={{
                  root: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                  wrapper: {
                    height: '100%',
                    flex: 1,
                  },
                  input: {
                    height: '100%',
                    minHeight: '100%',
                    flex: 1,
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    padding: '12px',
                    overflow: 'auto',
                    border: 'none',
                    resize: 'none',
                  },
                }}
              />
            </Tabs.Panel>
          </Tabs>
        )}
      </Box>
    </Box>
  );
}
